import React, { useContext, useMemo, useEffect, useRef } from 'react';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { trackEvent } from 'ddc-track-event';
import { IncentivesContext, UIContext } from '../contexts';
import BackToIncentivesLink from '../components/BackToIncentivesLink';
import VehicleImage from '../components/VehicleImage';
import VehicleTitle from '../components/VehicleTitle';
import EmptyState from '../components/EmptyState';
import SkeletonCard from '../components/SkeletonCard';
import OfferTypeTabs, {
	deriveAvailableTabs
} from '../components/OfferTypeTabs';
import IncentiveItem from '../components/IncentiveItem';
import { toSlug } from '../utils/toSlug';
import { WIDGET_STATES } from '../utils/trackingConstants';

const priceFormatter = new Intl.NumberFormat('en-US');

const DetailsView = () => {
	const prefs = usePrefs();
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const { selectedVehicleId, detailViewSelectedOfferTab, changeOfferTab } =
		useContext(UIContext);
	const { incentives, vehicles, loading } = useContext(IncentivesContext);

	const showImageAndDescription =
		prefs.showVehicleDescriptionAndImage !== 'false';
	const showPrice = prefs.showPrice === 'true';
	const showImageDisclaimer =
		prefs.showIncentivesImageDisclaimer === 'true' &&
		showImageAndDescription;

	const vehicleData = incentives.byVehicle[selectedVehicleId] || null;
	const gvData = vehicles[selectedVehicleId] || null;

	const trims = vehicleData?.trims;
	const startingPrice = useMemo(() => {
		if (!trims?.length) return null;
		const prices = trims.map((t) => t.msrp).filter((p) => p != null);
		return prices.length ? Math.min(...prices) : null;
	}, [trims]);

	const offers = useMemo(() => vehicleData?.incentives || [], [vehicleData]);
	const { useConditionalCategoryTypes } = prefs;
	const availableTabs = useMemo(
		() => deriveAvailableTabs(offers, prefs, labels),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[offers, useConditionalCategoryTypes] // prefs/labels are stable refs from wsm-common-data
	);

	// Track previous vehicle to avoid resetting on mount
	const prevVehicleRef = useRef(selectedVehicleId);

	// Story 9.2 (Rally F243497): read ?offerTypes= from URL at most ONCE per
	// detail-view mount and pre-select the matching tab. Subsequent vehicle
	// changes inside the same mount defer to the existing first-available
	// auto-select (see AC 5). On a non-matching value, fall back silently and
	// emit a stale-URL analytics event identical to the listing-view contract
	// (useDeepLinking.js). No AlertBanner is rendered on detail view — the
	// shopper arrived via deep link and has nothing to correct.
	const urlConsumedRef = useRef(false);
	// Transient flag: set true when the URL effect dispatches a tab so the
	// subsequent fallback effect in the same render pass does not clobber it.
	const urlJustDispatchedRef = useRef(false);

	// Reset tab when vehicle actually changes (not on mount)
	useEffect(() => {
		if (prevVehicleRef.current !== selectedVehicleId) {
			prevVehicleRef.current = selectedVehicleId;
			changeOfferTab(null);
		}
	}, [selectedVehicleId, changeOfferTab]);

	// Story 9.2: consume ?offerTypes on initial detail-view entry.
	// Runs before the auto-select fallback so its changeOfferTab call wins
	// the same-tick race (the fallback bails out when urlConsumedRef flips).
	useEffect(() => {
		if (urlConsumedRef.current) return;
		if (!isBrowser) return;
		if (availableTabs.length === 0) return;

		const params = new URLSearchParams(window.location.search);
		const raw = (params.get('offerTypes') || '').split(',')[0];
		if (!raw) {
			// No param — mark consumed so the fallback effect handles it.
			urlConsumedRef.current = true;
			return;
		}

		const slug = toSlug(raw);
		urlConsumedRef.current = true;

		if (!slug) {
			// Injection or all-symbols input normalized to empty — no match,
			// defer to the fallback effect without emitting a stale event
			// (empty slug ≠ stale deep link, likely a URL-construction bug).
			return;
		}

		const matchedTab = availableTabs.find(
			(t) => toSlug(t.incentiveTypeKey) === slug
		);
		if (matchedTab) {
			urlJustDispatchedRef.current = true;
			changeOfferTab(matchedTab.incentiveTypeKey);
			return;
		}

		// Slug present but no match — silent fallback + one stale-URL event.
		// No urlJustDispatchedRef flag here: the dispatched value equals
		// availableTabs[0] which is exactly what the fallback would pick,
		// so letting the fallback run (and no-op or re-set the same value)
		// is harmless. Emit the stale-URL analytics event for campaign ops.
		changeOfferTab(availableTabs[0].incentiveTypeKey);
		trackEvent(widgetName, windowId, {
			element: 'Stale URL Alert',
			action: 'displayed',
			result: 'total',
			nonInteractive: true,
			widgetState: WIDGET_STATES.DEFAULT
		});
	}, [availableTabs, changeOfferTab, widgetName, windowId]);

	// Auto-select first available tab when current tab is invalid.
	// urlJustDispatchedRef ensures that on the tick the Story 9.2 URL effect
	// dispatched a tab selection, this fallback does NOT clobber it. Reads
	// from context have not propagated yet in that same render pass.
	useEffect(() => {
		if (urlJustDispatchedRef.current) {
			urlJustDispatchedRef.current = false;
			return;
		}
		if (availableTabs.length > 0) {
			const currentValid = availableTabs.some(
				(t) => t.incentiveTypeKey === detailViewSelectedOfferTab
			);
			if (!currentValid) {
				changeOfferTab(availableTabs[0].incentiveTypeKey);
			}
		}
	}, [availableTabs, detailViewSelectedOfferTab, changeOfferTab]);

	const filteredOffers = useMemo(() => {
		if (!detailViewSelectedOfferTab) return offers;
		return offers.filter(
			(offer) =>
				offer.incentiveType.toLowerCase() === detailViewSelectedOfferTab
		);
	}, [offers, detailViewSelectedOfferTab]);

	const imageUrl = gvData?.vehicleMedia?.[0]?.url || null;

	const startingPriceLabel = labels.get('INCENTIVES_STARTING_PRICE');
	const msrpDisclaimerLabel = labels.get('INCENTIVES_MSRP_DISCLAIMER');
	const imageDisclaimerLabel = labels.get('INCENTIVES_IMAGES_DISCLAIMER');

	if (loading) {
		return (
			<div className="details-view">
				<BackToIncentivesLink />
				<SkeletonCard count={1} />
			</div>
		);
	}

	if (!vehicleData) {
		return (
			<>
				<div className="details-view">
					<BackToIncentivesLink />
				</div>
				<EmptyState type="noIncentives" />
			</>
		);
	}

	return (
		<div className="details-view">
			<BackToIncentivesLink />

			{/* Hero Section */}
			<div className="row">
				{showImageAndDescription && (
					<div className="col-12 col-md-5 mb-3 mb-md-0">
						<VehicleImage
							imageUrl={imageUrl}
							alt={`${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`}
						/>
						{showImageDisclaimer && (
							<p className="text-muted ddc-font-size-xsmall mt-1">
								{imageDisclaimerLabel}
							</p>
						)}
					</div>
				)}

				<div
					className={
						showImageAndDescription ? 'col-12 col-md-7' : 'col-12'
					}
				>
					<VehicleTitle vehicle={vehicleData} as="h1" />
					<p className="text-muted mb-2">
						{vehicleData?.year} &bull; {vehicleData?.bodyStyle}
					</p>

					{showImageAndDescription && gvData?.description && (
						<p className="mt-2 text-muted">{gvData.description}</p>
					)}

					{showPrice && startingPrice != null && (
						<>
							<p className="mt-2 ddc-font-size-large font-weight-bold">
								{startingPriceLabel} $
								{priceFormatter.format(startingPrice)}*
							</p>
							<p className="text-muted ddc-font-size-xsmall">
								* {msrpDisclaimerLabel}
							</p>
						</>
					)}
				</div>
			</div>

			{/* Offer Type Tabs + Filtered Content */}
			{offers.length > 0 && (
				<OfferTypeTabs
					offers={offers}
					activeTab={detailViewSelectedOfferTab}
					onTabSelect={changeOfferTab}
				>
					{filteredOffers.map((offer) => (
						<IncentiveItem
							key={offer.incentiveId}
							offer={offer}
							vehicle={vehicleData}
						/>
					))}
				</OfferTypeTabs>
			)}
		</div>
	);
};

export default DetailsView;
